import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';

import { apiPaths } from '../../../../../service/apiPaths';
import ApiService from '../../../../../service/ApiService';
import { formatDateToYYYYMMDD } from '../../../../../util/constant';
import { IAddEmployee, IEmployee } from '../../Employee.props';

type ArrayField =
  | "leave"
  | "employeeCareer"
  | "employeeQualification"
  | "familyDetail";

const useEditEmployee = (employeeId: string, setMethod: () => void) => {
  const [formData, setFormData] = useState<IAddEmployee>({
    name: "",
    email: "",
    empId: "",
    userEmail: "",
    subRole: "",
    doj: "",
    password:"",
    designation: "",
    department: "",
    phone: "",
    emergencyContact: "",
    adharCardNumber: "",
    currentAddress: "",
    permanentAddress: "",
    panCardNumber: "",
    bankAccountNo: "",
    bankName: "",
    bankIFSC: "",
    bankBranch: "",
    leave: [],
    previousCompany: {
      companyName: "",
      HREmailAddress: "",
      linkedinProfile: "",
    },
    employeeCareer: [],
    employeeQualification: [],
    familyDetail: [],
    academics: {
      stream: "",
      tenth: {
        maths: 0,
        science: 0,
        percentage: 0,
      },
      twelfth: {
        maths: 0,
        physics: 0,
        percentage: 0,
      },
    },
    documentsSubmitted: {
      offerLetter: false,
      payslip: false,
      relievingLetter: false,
      panCardCopy: false,
      adharCardCopy: false,
      educationProof: false,
      degreeCopy: false,
      experienceCertificates: false,
      marksheets: false,
      photoCopy: false,
      chequeCopy: false,
    },
    emailZipAttachment: false,
    slackId: "",
    slackChannelId: "",
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [employee, setEmployee] = useState<IEmployee | null>(null);
  const [image, setImage] = useState<File | null>(null);
  const [newPassword, setNewPassword] = useState<string>("");
  const [documents, setDocuments] = useState<{ [key: string]: File | null }>({
    businessAddressProof: null,
    gstProof: null,
    registerNo: null,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.getAllEmployees}/${employeeId}`,
      });
      setEmployee(response);
      populateFormData(response);
    } catch (error) {
      toast.error(`Error - ${error}`, {
        duration: 5000,
      });
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const populateFormData = (employeeData: IEmployee) => {
    setFormData({
      name: employeeData.name || "",
      email: employeeData.email || "",
      empId: employeeData.empId || "",
      userEmail: employeeData.userEmail || "",
      subRole: employeeData.subRole || "",
      doj: employeeData.doj || "",
      designation: employeeData.designation || "",
      department: employeeData.department || "",
      phone: employeeData.phone || "",
      emergencyContact: employeeData.emergencyContact || "",
      adharCardNumber: employeeData.adharCardNumber || "",
      currentAddress: employeeData.currentAddress || "",
      permanentAddress: employeeData.permanentAddress || "",
      panCardNumber: employeeData.panCardNumber || "",
      bankAccountNo: employeeData.bankAccountNo || "",
      bankName: employeeData.bankName || "",
      bankIFSC: employeeData.bankIFSC || "",
      bankBranch: employeeData.bankBranch || "",
      leave: employeeData.leave || [],
      previousCompany: {
        companyName: employeeData.previousCompany?.companyName || "",
        HREmailAddress: employeeData.previousCompany?.HREmailAddress || "",
        linkedinProfile: employeeData.previousCompany?.linkedinProfile || "",
      },
      employeeCareer: employeeData.employeeCareer || [],
      employeeQualification: employeeData.employeeQualification || [],
      familyDetail: employeeData.familyDetail || [],
      academics: {
        stream: employeeData.academics?.stream || "",
        tenth: {
          maths: employeeData.academics?.tenth?.maths || 0,
          science: employeeData.academics?.tenth?.science || 0,
          percentage: employeeData.academics?.tenth?.percentage || 0,
        },
        twelfth: {
          maths: employeeData.academics?.twelfth?.maths || 0,
          physics: employeeData.academics?.twelfth?.physics || 0,
          percentage: employeeData.academics?.twelfth?.percentage || 0,
        },
      },
      documentsSubmitted: {
        offerLetter: employeeData.documentsSubmitted?.offerLetter || false,
        payslip: employeeData.documentsSubmitted?.payslip || false,
        relievingLetter:
          employeeData.documentsSubmitted?.relievingLetter || false,
        panCardCopy: employeeData.documentsSubmitted?.panCardCopy || false,
        adharCardCopy: employeeData.documentsSubmitted?.adharCardCopy || false,
        educationProof:
          employeeData.documentsSubmitted?.educationProof || false,
        degreeCopy: employeeData.documentsSubmitted?.degreeCopy || false,
        experienceCertificates:
          employeeData.documentsSubmitted?.experienceCertificates || false,
        marksheets: employeeData.documentsSubmitted?.marksheets || false,
        photoCopy: employeeData.documentsSubmitted?.photoCopy || false,
        chequeCopy: employeeData.documentsSubmitted?.chequeCopy || false,
      },
      emailZipAttachment: employeeData.emailZipAttachment || false,
      slackId: employeeData.slackId || "",
      slackChannelId: employeeData.slackChannelId || "",
    });
  };
  const handleExcelUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json<any>(worksheet);

      if (jsonData.length === 0) {
        toast.error("No data found in the Excel file");
        return;
      }

      // Initialize new form data
      const newFormData: IAddEmployee = {
        ...formData,
        name: "",
        email: "",
        empId: "",
        userEmail: "",
        subRole: "",
        doj: "",
        designation: "",
        department: "",
        conformPassword: "",
        role: "employee",
        status: "active",
        password: "",
        phone: "",
        emergencyContact: "",
        adharCardNumber: "",
        panCardNumber: "",
        currentAddress: "",
        permanentAddress: "",
        bankAccountNo: "",
        bankName: "",
        bankIFSC: "",
        bankBranch: "",
        documentsSubmitted: {
          offerLetter: false,
          payslip: false,
          relievingLetter: false,
          panCardCopy: false,
          adharCardCopy: false,
          educationProof: false,
          degreeCopy: false,
          experienceCertificates: false,
          marksheets: false,
          photoCopy: false,
          chequeCopy: false,
        },
        previousCompany: {
          companyName: "",
          HREmailAddress: "",
          linkedinProfile: "",
        },
        leave: [],
        employeeCareer: [],
        employeeQualification: [],
        familyDetail: [],
        academics: {
          stream: "",
          tenth: {
            maths: 0,
            science: 0,
            percentage: 0,
          },
          twelfth: {
            maths: 0,
            physics: 0,
            percentage: 0,
          },
        },
        emailZipAttachment: false,
      };

      // Process each row from the Excel data
      let currentSection = "";
      let careerEntry: any = {};
      let qualificationEntry: any = {};
      let familyEntry: any = {};

      jsonData.forEach((row: any) => {
        const field = row["Personal Information"];
        const value = row["__EMPTY"];
        const value1 = row["__EMPTY_1"];
        const value2 = row["__EMPTY_2"];
        const value3 = row["__EMPTY_3"];
        const value4 = row["__EMPTY_4"];
        const value5 = row["__EMPTY_5"];
        const value6 = row["__EMPTY_6"];

        // Update current section
        if (field === "EMPLOYEE CAREER") {
          currentSection = "career";
        } else if (field === "EMPLOYEE QUALIFICATION") {
          currentSection = "qualification";
        } else if (field === "FAMILY DETAIL") {
          currentSection = "family";
        } else if (field === "ACADEMICS DETAILS") {
          currentSection = "academics";
        } else if (
          field === "DOCUMENTS (PDF File) * Make a zip file and attach in email"
        ) {
          currentSection = "documents";
        } else if (field === "COMPANY DETAIL") {
          currentSection = "company";
        }

        // Map fields based on current section
        switch (currentSection) {
          case "career":
            if (field === "Company") {
              // Skip header row
            } else if (field && value) {
              // Convert Excel date numbers to JavaScript dates
              const startDate = value1
                ? new Date(Math.round((value1 - 25569) * 86400 * 1000))
                : null;
              const endDate = value2
                ? new Date(Math.round((value2 - 25569) * 86400 * 1000))
                : null;

              console.log(
                "date",
                formatDateToYYYYMMDD(String(startDate)),
                endDate
              );
              const careerEntry = {
                company: field,
                city: value,
                startDate: formatDateToYYYYMMDD(String(startDate)),
                endDate: formatDateToYYYYMMDD(String(endDate)),
                joiningSalary: value3,
                lastSalary: value4,
                remarks: value5,
                duration: value6,
              };
              newFormData.employeeCareer.push(careerEntry);
            }
            break;

          case "qualification":
            if (field === "Degree") {
              // This is a header row
              qualificationEntry = {};
            } else if (field && value) {
              qualificationEntry = {
                degree: field,
                college: value,
                passYear: value1,
                university: value2,
                grade: value3,
                remarks: value4,
              };
              newFormData.employeeQualification.push(qualificationEntry);
            }
            break;

          case "family":
            if (field === "Relation") {
              // Skip header row
            } else if (field && value) {
              const familyMember = {
                Relation: field,
                Name: value,
                Industry: value1 || "",
                ContactNo: value2 || "",
              };
              newFormData.familyDetail.push(familyMember);
            }
            break;

          case "academics":
            if (field === "Stream") {
              newFormData.academics.stream = value;
            } else if (field === "10th Maths") {
              newFormData.academics.tenth.maths = Number(value);
            } else if (field === "10th Science:*") {
              newFormData.academics.tenth.science = Number(value);
            } else if (field === "10th %") {
              newFormData.academics.tenth.percentage = Number(value);
            } else if (field === "12th Maths") {
              newFormData.academics.twelfth.maths = Number(value);
            } else if (field === "12th Physics :*") {
              newFormData.academics.twelfth.physics = Number(value);
            } else if (field === "12th %") {
              newFormData.academics.twelfth.percentage = Number(value);
            }
            break;

          case "documents":
            if (
              field === "Offer Letter/ Appointment Letter of all past employers"
            ) {
              newFormData.documentsSubmitted.offerLetter = value1 === "Yes";
            } else if (field === "Payslip for last 3 months") {
              newFormData.documentsSubmitted.payslip = value1 === "Yes";
            } else if (
              field ===
              "Acceptance of Resignation / Relieving letter from previous employer"
            ) {
              newFormData.documentsSubmitted.relievingLetter = value1 === "Yes";
            } else if (field === "Copy of PAN Card") {
              newFormData.documentsSubmitted.panCardCopy = value1 === "Yes";
            } else if (field === "Copy of Aadhar Card") {
              newFormData.documentsSubmitted.adharCardCopy = value1 === "Yes";
            } else if (
              field ===
              "Proof of educational Qualification from 10th standard onwards"
            ) {
              newFormData.documentsSubmitted.educationProof = value1 === "Yes";
            } else if (field === "Copy of Degree") {
              newFormData.documentsSubmitted.degreeCopy = value1 === "Yes";
            } else if (field === "Copy of experience certificates") {
              newFormData.documentsSubmitted.experienceCertificates =
                value1 === "Yes";
            } else if (field === "Copy of mark sheets") {
              newFormData.documentsSubmitted.marksheets = value1 === "Yes";
            } else if (field === "Photo Copy ") {
              newFormData.documentsSubmitted.photoCopy = value1 === "Yes";
            } else if (field === "Your One Cheque ") {
              newFormData.documentsSubmitted.chequeCopy = value1 === "Yes";
            }
            break;

          case "company":
            if (field === "Employee ID") {
              newFormData.empId = value;
            } else if (field === "Designation") {
              newFormData.designation = value;
            } else if (field === "Department") {
              newFormData.department = value;
            } else if (field === "Joining Date") {
              if (value) {
                const date = new Date(
                  Math.round((value - 25569) * 86400 * 1000)
                );
                newFormData.doj = formatDateToYYYYMMDD(String(date));
              }
            }
            break;

          default:
            // Map basic fields
            switch (field) {
              case "Full Name":
                newFormData.name = value;
                break;
              case "Email Id":
                newFormData.userEmail = value;
                break;
              case "Company Email Id":
                newFormData.email = value;
                break;
              case "Phone":
                newFormData.phone = value;
                break;
              case "Emergency Contact":
                newFormData.emergencyContact = value;
                break;
              case "Adhar Card Number":
                newFormData.adharCardNumber = value;
                break;
              case "Full Current home address":
                newFormData.currentAddress = value;
                break;
              case "Permanent home address":
                newFormData.permanentAddress = value;
                break;
              case "Pan Card Number":
                newFormData.panCardNumber = value;
                break;
              case "Bank Account No":
                newFormData.bankAccountNo = value;
                break;
              case "Bank Name":
                newFormData.bankName = value;
                break;
              case "Bank IFSC":
                newFormData.bankIFSC = value;
                break;
              case "Bank Branch":
                newFormData.bankBranch = value;
                break;
              case "Name":
                newFormData.previousCompany.companyName = value;
                break;
              case "HR email address":
                newFormData.previousCompany.HREmailAddress = value;
                break;
              case "Your LinkedIn Profile":
                newFormData.previousCompany.linkedinProfile = value;
                break;
            }
            break;
        }
      });

      setFormData(newFormData);
      toast.success("Excel data imported successfully");
    } catch (error) {
      console.error("Error processing Excel file:", error);
      toast.error("Failed to process Excel file");
    }
  };
  const handleChange = (
    e:
      | ChangeEvent<HTMLInputElement>
      | { target: { id: string; value: any; type?: string } },
    path?: string[],
    value?: any
  ) => {
    if ("type" in e) {
      const { id, value, type, checked } = e.target;
      if (type === "checkbox") {
        setFormData((prev) => ({
          ...prev,
          documentsSubmitted: {
            ...prev.documentsSubmitted,
            [id]: checked,
          },
        }));
      } else {
        setFormData((prev) => ({ ...prev, [id]: value }));
      }
    } else if (path && value !== undefined) {
      setFormData((prev) => {
        const newData = { ...prev };
        let current: any = newData;

        for (let i = 0; i < path.length - 1; i++) {
          current = current[path[i]];
        }

        current[path[path.length - 1]] = value;
        return newData;
      });
    }
  };

  const handleArrayChange = (
    field: ArrayField,
    index: number,
    key: string,
    value: any
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].map((item: any, i: number) =>
        i === index ? { ...item, [key]: value } : item
      ),
    }));
  };

  const addArrayItem = (field: ArrayField, template: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], template],
    }));
  };

  const removeArrayItem = (field: ArrayField, index: number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_: any, i: number) => i !== index),
    }));
  };

  const handleSubmit: SubmitHandler<IAddEmployee> = async () => {
    setLoading(true);
    try {
      const response = await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.updateEmployee}${employeeId}`,
        data: formData,
      });
      if (response !== "undefined") {
        toast.success("Employee updated successfully", {
          duration: 5000,
        });
        setTimeout(() => {
          setMethod();
        }, 1500);
      }
    } catch (error) {
      toast.error(`Error - ${error}`, {
        duration: 5000,
      });
      console.error("Error updating employee:", error);
    } finally {
      setLoading(false);
    }
  };
  const handlePasswordChange = async () => {
    if (!newPassword) {
      toast.error("Please enter a new password");
      return;
    }

    setLoading(true);
    try {
      const response = await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.updateEmployee}${employeeId}`,
        data: { password: newPassword },
      });
      
      if (response) {
        toast.success("Password updated successfully", {
          duration: 5000,
        });
        setNewPassword("");
        setTimeout(() => {
          setMethod();
        }, 1500);
      }
    } catch (error) {
      toast.error(`Error - ${error}`, {
        duration: 5000,
      });
      console.error("Error updating password:", error);
    } finally {
      setLoading(false);
    }
  };

  const onDrop = (event: ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const files = event.target.files;
    if (files && files[0]) {
      if (fieldName === "logo") {
        setImage(files[0]);
      } else {
        setDocuments((prevDocs) => ({ ...prevDocs, [fieldName]: files[0] }));
      }
    }
  };

  const removeImage = () => {
    setImage(null);
    setFormData((prevState) => ({
      ...prevState,
      logo: "",
    }));
  };

  const handleSlackConnect = async () => {
    if (!formData.slackId) {
      toast.error("Please enter a Slack ID first");
      return;
    }

    setLoading(true);
    try {
      const response = await ApiService({
        method: "POST",
        endpoint: apiPaths.slackConversationsOpen,
        data: {
          receiverId: formData.slackId,
        },
      });

      // Assuming the API returns a channel ID in the response
      // The response structure might vary, so we'll check for common field names
      const channelId = response?.channel?.id || response?.channelId || response?.id || response?.channel;
      
      if (channelId) {
        const updatedFormData = {
          ...formData,
          slackChannelId: channelId,
        };
        setFormData(updatedFormData);
        
        // Auto-save the data after connecting
        try {
          const saveResponse = await ApiService({
            method: "PUT",
            endpoint: `${apiPaths.updateEmployee}${employeeId}`,
            data: updatedFormData,
          });
          if (saveResponse !== "undefined") {
            toast.success("Slack channel connected and data saved successfully", {
              duration: 5000,
            });
          }
        } catch (saveError) {
          toast.error(`Error saving data - ${saveError}`, {
            duration: 5000,
          });
          console.error("Error saving employee:", saveError);
        }
      } else {
        toast.error("Failed to get channel ID from response");
      }
    } catch (error) {
      toast.error(`Error connecting to Slack - ${error}`, {
        duration: 5000,
      });
      console.error("Error connecting to Slack:", error);
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    formData,
    image,
    employee,
    fileInputRef,
    documents,
    onDrop,
    removeImage,
    handleChange,
    handleArrayChange,
    addArrayItem,
    removeArrayItem,
    handleSubmit,
    handleExcelUpload,
    handlePasswordChange,
    newPassword,
    setNewPassword,
    handleSlackConnect,
  };
};

export default useEditEmployee;
